import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Pencil, X } from "lucide-react";
import { Button } from "../../base/button/button";
import { useTranslation } from "react-i18next";
import { ACTION_TYPE_ICONS } from "./stepFieldIcons";
export function StepDetailPanel({ children }) {
    // The panel renders as a React Flow overlay; FlowCanvas sets `cursor: grab`
    // for pan mode and CSS cursor inheritance leaks it into every child of the
    // sidebar. Reset to `default` here so static content shows the arrow and
    // form controls / buttons fall back to their own cursors.
    return /*#__PURE__*/ _jsx("div", {
        className: "bg-white rounded-lg shadow-lg border border-line flex flex-col max-h-full overflow-hidden cursor-default",
        children: children
    });
}
export function StepDetailBody({ children }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-detail-scroll": "",
        className: "px-4 py-3 flex-1 overflow-y-auto min-h-0",
        children: children
    });
}
export function StepDetailHeader({ step, onClose, hideDescription, editableName, editableDescription, headerAction, nameSlot, children, stepNumber }) {
    const { t } = useTranslation();
    const isTask = step.actionType === "task";
    const titleColor = isTask ? "text-info-strong" : "text-highlight-strong";
    const TypeIcon = isTask ? ACTION_TYPE_ICONS.task : ACTION_TYPE_ICONS.decision;
    const iconColor = isTask ? "text-info" : "text-highlight";
    const fallbackTitle = t("processFlow.stepDetails", {
        number: step.stepNumber || 0
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: `px-4 py-3 border-b ${isTask ? "bg-info-surface border-info-line" : "bg-highlight-surface border-highlight-line"}`,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 min-w-0 flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(TypeIcon, {
                                className: `h-4 w-4 shrink-0 ${iconColor}`,
                                strokeWidth: 2
                            }),
                            stepNumber != null && /*#__PURE__*/ _jsx("span", {
                                className: "shrink-0 inline-flex items-center rounded bg-white/70 px-1.5 py-0.5 text-3xs font-semibold text-fg-secondary",
                                children: t("processFlow.stepPrefix", {
                                    number: stepNumber
                                })
                            }),
                            editableName ? /*#__PURE__*/ _jsxs("div", {
                                className: "relative flex-1 min-w-0 group",
                                children: [
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "text",
                                        value: editableName.value,
                                        onChange: (e)=>editableName.onChange(e.target.value),
                                        onBlur: editableName.onBlur,
                                        placeholder: editableName.placeholder ?? fallbackTitle,
                                        className: `w-full bg-transparent text-sm font-semibold ${titleColor} placeholder:text-fg-subtle placeholder:font-normal focus:outline-none focus:ring-1 focus:ring-info-solid rounded-sm px-0.5 -mx-0.5 pr-5`
                                    }),
                                    /*#__PURE__*/ _jsx(Pencil, {
                                        className: "absolute right-1 top-1/2 -translate-y-1/2 h-3 w-3 text-fg-subtle pointer-events-none opacity-60 group-focus-within:opacity-0",
                                        strokeWidth: 2
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx("h3", {
                                className: `text-sm font-semibold ${titleColor} truncate`,
                                children: step.generatedName || fallbackTitle
                            }),
                            nameSlot
                        ]
                    }),
                    headerAction,
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "ghost",
                        size: "small",
                        onClick: onClose,
                        className: "p-1! h-6! w-6! shrink-0",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "h-4 w-4"
                        })
                    })
                ]
            }),
            !hideDescription && (editableDescription ? /*#__PURE__*/ _jsx("textarea", {
                value: editableDescription.value,
                onChange: (e)=>editableDescription.onChange(e.target.value),
                onBlur: editableDescription.onBlur,
                placeholder: editableDescription.placeholder,
                rows: 2,
                className: `w-full mt-1 bg-transparent text-xs ${isTask ? "text-info/70" : "text-highlight/70"} placeholder:text-fg-subtle focus:outline-none focus:ring-1 focus:ring-info-solid rounded-sm px-0.5 -mx-0.5 resize-none`
            }) : step.generatedDescription ? /*#__PURE__*/ _jsx("p", {
                className: `text-xs mt-1 ${isTask ? "text-info/70" : "text-highlight/70"}`,
                children: step.generatedDescription
            }) : null),
            children
        ]
    });
}
export function StepDetailTabs({ tabs, activeTab, onTabChange }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "flex gap-3 mt-2 -mb-3 px-0",
        children: tabs.map((tab)=>/*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: ()=>onTabChange(tab.key),
                className: `pb-2 text-xs font-medium border-b-2 transition-colors ${activeTab === tab.key ? "border-info-solid text-info" : "border-transparent text-muted-foreground hover:text-fg-secondary"}`,
                children: [
                    tab.label,
                    tab.badge != null && tab.badge > 0 && /*#__PURE__*/ _jsx("span", {
                        className: "ml-1 text-xs bg-info-surface-strong text-info px-1.5 py-0.5 rounded-full",
                        children: tab.badge
                    })
                ]
            }, tab.key))
    });
}

//# sourceMappingURL=StepDetailBuildingBlocks.js.map